import { motion, type Variants } from 'framer-motion';
import {
  ArrowRight,
  ClipboardList,
  FolderOpen,
  Shield,
  ShoppingCart,
  Siren,
  Video,
  type LucideIcon,
} from 'lucide-react';
import { products } from '../data/mockData';
import type { Product } from '../types';

const ICON_MAP: Record<string, LucideIcon> = {
  shield: Shield,
  siren: Siren,
  video: Video,
  cart: ShoppingCart,
  clipboard: ClipboardList,
  folder: FolderOpen,
};

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

interface ProductsProps {
  onContact: () => void;
}

function ProductCard({
  product,
  onContact,
}: {
  product: Product;
  onContact: () => void;
}) {
  const Icon = ICON_MAP[product.icon] ?? Shield;

  return (
    <motion.div
      variants={item}
      className="group relative flex flex-col rounded-2xl border border-slate-800 bg-slate-900/60 p-8 glow-card"
    >
      {/* 顶部渐变光带 */}
      <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      <div className="flex h-6 w-6 items-center justify-center rounded-xl transition-transform duration-500 group-hover:scale-110">
        <Icon className="h-5 w-5 text-white" />
      </div>

      <h3 className="mt-6 text-xl font-bold text-white">{product.name}</h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">
        {product.description}
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {product.tags.map((tag) => (
          <span key={tag} className="rounded-full border border-slate-700 bg-slate-800/80 px-3 py-1 text-xs text-slate-300 transition-colors duration-300 group-hover:border-cyan-500/40 group-hover:text-cyan-300" >
            {tag}
          </span>
        ))}
      </div>

      <button
        type="button"
        onClick={onContact}
        className="mt-7 inline-flex translate-y-2 items-center gap-2 text-sm font-semibold text-cyan-400 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 hover:gap-3 hover:text-cyan-300"
      >
        了解详情
        <ArrowRight className="h-4 w-4" />
      </button>
    </motion.div>
  );
}

export default function Products({ onContact }: ProductsProps) {
  return (
    <section id="products" className="relative bg-slate-950 py-24">
      {/* 网格纹理装饰 */}
      <div className="grid-texture pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={container} className="mx-auto max-w-2xl text-center" >
          <motion.p variants={item} className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-400" >
            Products
          </motion.p>
          <motion.h2 variants={item} className="mt-3 text-3xl font-bold text-white sm:text-4xl lg:text-5xl" >
            核心产品
          </motion.h2>
          <motion.p variants={item} className="mt-4 text-slate-400" >
            以安全为底座，覆盖基层治理、应急指挥与智能分析的全场景产品矩阵
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }} variants={container} className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" >
          {products.map((product) => (
            <ProductCard key={product.id} product={product} onContact={onContact} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
